<template>
  <div
    :key="renderTrigger"
    ref="dropdown"
    class="mr-8 mt-2 dropdown v-card v-sheet theme--light sn-shadow-depth-1 white-background"
    data-cy="profile-dropdown"
  >
    <ul class="dropdown-list px-0 py-2 ma-0">
      <template v-if="showOrgOptions">
        <profile-dropdown-option
          v-if="activeOrg != null"
          data-cy="active-org"
          :main-text="activeOrg.value"
          :max-text-length="maxTextLength"
          :subtext="activeOrg.subtext"
          :clickable="false"
          :bold="true"
        />

        <profile-dropdown-option
          v-if="maySwitchToProduction"
          main-text="Switch to production"
          icon="$snTabArrows"
          data-cy="switch-environment"
          :show-avatar="false"
          @click.native="handleOptionClick(switchToProductionOption)"
        />

        <profile-dropdown-option
          v-if="maySwitchToStaging"
          main-text="Switch to staging"
          icon="$snTabArrows"
          data-cy="switch-environment"
          :show-avatar="false"
          @click.native="handleOptionClick(switchToStagingOption)"
        />

        <div
          v-if="hasOtherOrgs"
          class="dropdown-section"
        >
          <v-divider />
          <p class="dropdown-subtitle sn-subtitle-2">
            Organizations
          </p>
          <profile-dropdown-option
            v-for="(org, index) in otherOrgs"
            :key="index"
            :main-text="org.value"
            :max-text-length="maxTextLength"
            :subtext="org.subtext"
            @click.native="handleOptionClick(org)"
          />
        </div>
      </template>

      <div
        v-if="userGuid"
        class="dropdown-section"
      >
        <v-divider v-if="showOrgOptions" />
        <p class="dropdown-subtitle sn-subtitle-2">
          Profile
        </p>
        <profile-dropdown-option
          :image="profilePictureUrl"
          :main-text="userName"
          :clickable="false"
          :subtext="roleType"
        />
        <profile-dropdown-option
          :main-text="'My profile'"
          :icon="'$snPerson'"
          :show-avatar="false"
          @click.native="handleOptionClick(profileSettingsOption)"
        />
      </div>

      <v-divider />

      <profile-dropdown-option
        main-text="Sign out"
        :show-avatar="false"
        @click.native="handleOptionClick(signOutOption)"
      />
    </ul>
  </div>
</template>

<script>
import ProfileDropdownOption from './profileDropdownOption.vue'
import { mapGetters, mapMutations } from 'vuex'
import snEvaultService from '../../services/snEvaultService'
import _ from 'lodash'

export default {
  name: 'ProfileDropdown',
  components: {
    ProfileDropdownOption
  },
  props: {
    userName: {
      type: String,
      default: '',
      required: false
    },
    userGuid: {
      type: String,
      default: '',
      required: true
    },
    profilePictureUrl: {
      type: String,
      default: '',
      required: false
    },
    isShown: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      orgOptions: [],
      signOutOption: { value: 'sign_out', action: 'logout' },
      switchToProductionOption: { value: false, action: 'set_is_staging' },
      switchToStagingOption: { value: true, action: 'set_is_staging' },
      profileSettingsOption: { value: 'profile_settings', action: 'profile_settings' },
      profileSettingsVisible: false,
      renderTrigger: 0,
      maxTextLength: 20,
      mayClose: false // prevents click event that opened the dropdown from immediately closing the dropdown
    }
  },
  computed: {
    ...mapGetters(['user', 'isStaging', 'orgs', 'orgGuid', 'viewAsCompany', 'appInitialized', 'mersOrgId']),
    activeOrg () {
      return this.orgOptions.find(opt => opt.guid === this.orgGuid)
    },
    otherOrgs () {
      return this.orgOptions
        .filter(opt => opt.guid !== this.orgGuid)
        .sort((a, b) => a.remoteId - b.remoteId)
    },
    hasOtherOrgs () {
      return this.otherOrgs.length > 0
    },
    maySwitchToStaging () {
      return !this.isStaging && (snEvaultService.hasPermission(this.user, 'stagingEnabled'))
    },
    maySwitchToProduction () {
      return this.isStaging && (snEvaultService.hasPermission(this.user, 'productionEnabled'))
    },
    showOrgOptions () {
      return !this.user.isSnAdmin || this.viewAsCompany
    },
    companyGuid () {
      if (this.user.isSnAdmin) {
        return this.viewAsCompany ? this.viewAsCompany.guid : undefined
      }
      return undefined
    },
    roleType () {
      if (this.user.accountType) {
        return _.capitalize(this.user.accountType.replaceAll('_', ' ')).replace('Evault', 'eVault')
      }
      return ''
    }
  },
  watch: {
    orgs () {
      this.initializeOrgOptions()
    }
  },
  created () {
    document.addEventListener('click', this.clickAway)
    this.initializeOrgOptions()
  },
  beforeDestroy () {
    document.removeEventListener('click', this.clickAway)
  },
  methods: {
    ...mapMutations(['setIsStaging', 'setMersOrgId', 'setOrgGuid', 'setProfileSettingsVisible', 'setProfileSettingsInitialTab']),
    handleOptionClick (option) {
      // reroute user to current base route if on details pages
      const matchedPath = this.$route.matched[0].path
      const basePath = matchedPath.split(':')[0]

      switch (option.action) {
        case 'switch_org':
          this.switchOrg(option)
          this.close()
          if (basePath !== matchedPath) {
            this.$router.push(basePath)
          }
          const canAccess = snEvaultService.canAccess(this.user, this.$route.name, !!this.mersOrgId)
          if (!canAccess) {
            this.$router.push(snEvaultService.getDefaultPage(this.user, !!this.mersOrgId))
          }
          break
        case 'set_is_staging':
          this.switchEnvironment(option.value)
          this.close()
          if (basePath !== matchedPath) {
            this.$router.push(basePath)
          }
          break
        case 'profile_settings':
          this.setProfileSettingsInitialTab(0)
          this.setProfileSettingsVisible(true)
          this.close()
          break
        case 'logout':
          this.$emit('logout')
          break
        default:
          break
      }
    },
    initializeOrgOptions () {
      if (this.orgs == null) {
        this.orgOptions = []
        return
      }

      this.orgOptions = this.orgs.map(org => {
        let value = org.remoteId
        let subtext = ''
        if (org.name != null && org.name.length > 0 && org.name !== org.remoteId) {
          value = org.name
          subtext = org.remoteId
        }
        return { guid: org.guid, remoteId: org.remoteId, value: value, subtext: subtext, action: 'switch_org', active: org.active }
      })
    },
    switchOrg (option) {
      this.setOrgGuid(option.guid)
      this.setMersOrgId(option.remoteId)
    },
    switchEnvironment (isStaging) {
      this.setIsStaging(isStaging)
    },
    toggleProfileSettings () {
      this.profileSettingsVisible = !this.profileSettingsVisible
    },
    clickAway (event) {
      if (this.isShown) {
        if (this.mayClose) {
          const { clientX, clientY } = event
          const bounds = this.$refs.dropdown.getBoundingClientRect()

          if (!this.isWithin(clientX, clientY, bounds)) {
            this.close()
          }
        } else {
          this.mayClose = true
        }
      }
    },
    close () {
      this.mayClose = false
      this.$emit('close')
    },
    isWithin (clientX, clientY, bounds) {
      if (clientX >= bounds.x && clientX <= (bounds.x + bounds.width)) {
        if (clientY >= bounds.y && clientY <= (bounds.y + bounds.height)) {
          return true
        }
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.dropdown {
  border-radius: 0;
  width: 250px;
  position: absolute;
  right: -5px;
  top: 70px;
  z-index: 1000;
  user-select: none;

  &-list {
    display: flex;
    flex-direction: column;
    list-style-type: none;
    text-align: left;

    &-item {
      white-space: nowrap;
      padding: 10px 16px;
      margin-left: -1px;
      line-height: 24px;
      cursor: pointer;

      &:hover {
        background: rgba(15, 95, 94, 0.05);
      }
    }
  }
  &-subtitle {
    text-transform: uppercase;
    color: $sn-gray-04;
    margin: 12px 16px 4px 16px;
  }
}

.dropdown-section {
  display: flex;
  flex-direction: column;
}

</style>
